const imageCache = new Map();
function loadImage(src) {
  if (!imageCache.has(src)) {
    imageCache.set(src, new Promise((resolve, reject) => {
      let attempt = 0;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = error => {
        if (attempt++ < 2 && !src.startsWith('blob:')) {
          setTimeout(() => { img.src = src; }, attempt * 400);
          return;
        }
        imageCache.delete(src); reject(error);
      };
      img.src = src;
    }));
  }
  return imageCache.get(src);
}
const printAreas = {
  Rectangle: {box:[330,326,300,508], portrait:[344,345,268,445], name:[480,728,215]},
  Round: {box:[252,408,478,475], portrait:[377,439,229,378], name:[491,790,300]},
  Heart: {box:[225,407,535,480], portrait:[390,464,205,340], name:[492,766,265]}
};
class PetPreview extends HTMLElement {
  static observedAttributes = ['shape', 'background', 'pet-name', 'photo', 'background-removed'];
  connectedCallback() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = 960;
    this.canvas.setAttribute('role', 'img');
    this.canvas.setAttribute('aria-label', this.getAttribute('aria-label') || 'Personalized pet keychain preview');
    this.replaceChildren(this.canvas);
    this.draw();
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'photo' && oldValue !== newValue && oldValue?.startsWith('blob:')) imageCache.delete(oldValue);
    if (this.canvas) this.draw();
  }
  disconnectedCallback() {
    this.version = (this.version || 0) + 1;
    const photo = this.getAttribute('photo');
    if (photo?.startsWith('blob:')) imageCache.delete(photo);
  }
  async draw() {
    const version = this.version = (this.version || 0) + 1;
    this.dataset.ready = 'false';
    const shape = printAreas[this.getAttribute('shape')] ? this.getAttribute('shape') : 'Rectangle';
    const color = (this.getAttribute('background') || 'Tricolor').toLowerCase();
    const photo = this.getAttribute('photo');
    const assets = JSON.parse(this.dataset.assets || '{}');
    const asset = (file) => assets[file] || '/assets/' + file;
    try {
      const [base, overlay, background, pet] = await Promise.all([
        loadImage(asset(shape.toLowerCase() + '-base.webp')),
        loadImage(asset(shape.toLowerCase() + '-overlay.webp')),
        loadImage(asset('background-' + color + '-artwork.webp')),
        loadImage(photo || asset('sample-pet.webp'))
      ]);
      await document.fonts.load('36px StudioScript');
      if (version !== this.version || !this.isConnected) return;
      const ctx = this.canvas.getContext('2d');
      const area = printAreas[shape];
      ctx.clearRect(0, 0, 960, 960);
      ctx.drawImage(base, 0, 0, 960, 960);
      ctx.drawImage(background, ...area.box);
      const [x, y, w, h] = area.portrait;
      const scale = Math.min(w / pet.width, h / pet.height);
      let portrait = pet;
      if (photo && this.getAttribute('background-removed') === 'true') {
        const blend = document.createElement('canvas');
        blend.width = pet.width; blend.height = pet.height;
        const blendCtx = blend.getContext('2d');
        blendCtx.drawImage(pet, 0, 0);
        blendCtx.globalCompositeOperation = 'destination-in';
        const fade = blendCtx.createLinearGradient(0, 0, 0, blend.height);
        fade.addColorStop(0, '#fff'); fade.addColorStop(.86, '#fff'); fade.addColorStop(1, 'transparent');
        blendCtx.fillStyle = fade; blendCtx.fillRect(0, 0, blend.width, blend.height);
        portrait = blend;
      }
      ctx.drawImage(portrait, x + (w - pet.width * scale) / 2, y, pet.width * scale, pet.height * scale);
      const name = (this.getAttribute('pet-name') || '').trim();
      if (name) {
        const [nx, ny, nw] = area.name;
        ctx.font = '500 43px StudioScript';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fff9ec';
        ctx.shadowColor = '#fff9ec';
        ctx.shadowBlur = 9;
        ctx.fillText(name, nx, ny, nw);
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#342b23';
        ctx.fillText(name, nx, ny, nw);
      }
      ctx.drawImage(overlay, 0, 0, 960, 960);
      this.dataset.ready = 'true';
      this.dataset.rendered = [shape, color, name, photo ? 'photo' : 'sample'].join('|');
      this.dispatchEvent(new CustomEvent('preview-ready', {bubbles:true}));
    } catch {
      if (version !== this.version) return;
      this.dataset.ready = 'error';
      this.dispatchEvent(new CustomEvent('preview-error', {bubbles:true}));
    }
  }
}
if (!customElements.get('pet-preview')) customElements.define('pet-preview', PetPreview);
